/* eslint-disable jsx-a11y/anchor-is-valid, import/prefer-default-export */
/* eslint-disable react/jsx-filename-extension */
import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Context } from '../../Context/AuthContext';
import { hasPermissao, PERMISSOES_TAREFAS_CONTATO } from '../../utils/acl/permissoes';
import useTarefasContatoPendentesCount from '../../hooks/useTarefasContatoPendentesCount';
import useTarefasContato from '../../services/api/tarefasContato';
import useNovidades from '../../hooks/useNovidades';
import NovidadesPopup from '../NovidadesPopup';

const MAX_TAREFAS_NO_SINO = 5;

// Link de atendimento/suporte, mesmo destino usado antes na Sidebar (ver
// src/components/Sidebar/index.js) — trocar de canal não deveria exigir
// mexer em layout/nome de variável.
const URL_SUPORTE = 'https://wa.me/5521978364416';

// Topbar com os itens comuns a qualquer usuário logado, independente de
// setor/permissão: sino de Tarefas de Contato pendentes, Ajuda, Ajuda do
// Sistema (WhatsApp) e Perfil/Sair. Os dois dropdowns (sino/perfil) são
// controlados por estado próprio, não pelo data-toggle="dropdown" do
// template — o pacote "bootstrap" instalado aqui é a v5, que não usa jQuery
// e só reconhece data-bs-toggle; o data-toggle (sintaxe v4) do template SB
// Admin 2 nunca teve efeito algum, então clicar no sino/perfil mudava só o
// hash da URL sem abrir nada (bug real encontrado em produção 2026-09-28).
export function Navbar() {
  const { handleLogout } = useContext(Context);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navRef = useRef(null);

  useEffect(() => {
    const handleClickFora = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickFora);
    return () => document.removeEventListener('mousedown', handleClickFora);
  }, []);

  const toggleDropdown = (nome) => (event) => {
    event.preventDefault();
    setOpenDropdown((atual) => (atual === nome ? null : nome));
  };

  const podeVerTarefas = hasPermissao(PERMISSOES_TAREFAS_CONTATO);
  const { naoLidas: novidadesNaoLidas } = useNovidades();
  const {
    total: tarefasPendentesTotal, semanaAtual,
  } = useTarefasContatoPendentesCount({ ativo: podeVerTarefas });
  // unidadeId inexistente (-1) até semanaAtual carregar (uma chamada async a
  // /profile/view-profile dentro do hook acima) — evita disparar um primeiro
  // GET /tarefas-contato sem filtro nenhum, que devolveria todas as tarefas
  // do sistema por uma fração de segundo antes do filtro real chegar.
  const { tarefas } = useTarefasContato({
    status: 'PENDENTE',
    unidadeId: semanaAtual?.unidadeId ?? -1,
    dataPrevistaAte: semanaAtual?.dataPrevistaAte,
  }, { ativo: podeVerTarefas });
  const tarefasNoSino = [...tarefas]
    .sort((a, b) => (a.dataPrevista || '').localeCompare(b.dataPrevista || ''))
    .slice(0, MAX_TAREFAS_NO_SINO);
  const hoje = format(new Date(), 'yyyy-MM-dd');

  const tarefasContatoHref = semanaAtual
    ? `/tarefas-contato?status=PENDENTE&unidadeId=${semanaAtual.unidadeId}&dataPrevistaAte=${semanaAtual.dataPrevistaAte}`
    : '/tarefas-contato';

  const image = localStorage.getItem('image');
  const name = localStorage.getItem('name') || 'Perfil';

  return (
    <nav className="navbar navbar-expand navbar-light bg-white topbar mb-4 static-top shadow">
      {/* Mesmo evento que o botão "Config" do BottomNav já dispara (ver
          src/components/BottomNav/index.jsx) - a Sidebar só abre no mobile via a
          classe sidebar-mobile-expanded, controlada pelo estado interno dela mesma
          (src/components/Sidebar/index.js), não por classList.toggle direto aqui. */}
      <button
        id="sidebarToggleTop"
        type="button"
        className="btn btn-link d-md-none rounded-circle mr-3"
        onClick={() => window.dispatchEvent(new CustomEvent('toggle-sidebar-mobile'))}
      >
        <i className="fa fa-bars" />
      </button>

      <ul className="navbar-nav ml-auto" ref={navRef}>
        {podeVerTarefas && (
          <li className="nav-item dropdown no-arrow mx-1">
            <a
              className="nav-link dropdown-toggle"
              href="#tarefasContato"
              id="tarefasContatoDropdown"
              role="button"
              onClick={toggleDropdown('tarefasContato')}
              aria-haspopup="true"
              aria-expanded={openDropdown === 'tarefasContato'}
            >
              <i className="fas fa-bell fa-fw" />
              {tarefasPendentesTotal > 0 && (
                <span className="badge badge-danger badge-counter">{tarefasPendentesTotal}</span>
              )}
            </a>
            <div
              className={`dropdown-list dropdown-menu dropdown-menu-right shadow animated--grow-in${openDropdown === 'tarefasContato' ? ' show' : ''}`}
              aria-labelledby="tarefasContatoDropdown"
            >
              <h6 className="dropdown-header">Tarefas de Contato</h6>
              {tarefasNoSino.length === 0 ? (
                <span className="dropdown-item text-gray-500">Nenhuma tarefa pendente esta semana</span>
              ) : (
                tarefasNoSino.map((tarefa) => {
                  const atrasada = tarefa.dataPrevista && tarefa.dataPrevista < hoje;
                  return (
                    <Link
                      key={tarefa.id}
                      className="dropdown-item d-flex align-items-center"
                      to={`/edit-pessoa/${tarefa.pessoaId}`}
                      onClick={() => setOpenDropdown(null)}
                    >
                      <div className="mr-3">
                        <div className={`icon-circle ${atrasada ? 'bg-warning' : 'bg-primary'}`}>
                          <i className="fas fa-phone-volume text-white" />
                        </div>
                      </div>
                      <div>
                        <div className="small text-gray-500">
                          {tarefa.dataPrevista
                            ? format(new Date(`${tarefa.dataPrevista}T00:00:00`), 'dd/MM/yyyy', { locale: ptBR })
                            : 'Sem data prevista'}
                          {atrasada && ' · Atrasada'}
                        </div>
                        <span className="font-weight-bold">{tarefa.pessoa?.nome || 'Pessoa não identificada'}</span>
                        {tarefa.tipotarefacontato?.nome && (
                          <span className="text-gray-500">
                            {' — '}
                            {tarefa.tipotarefacontato.nome}
                          </span>
                        )}
                      </div>
                    </Link>
                  );
                })
              )}
              <Link
                className="dropdown-item text-center small text-gray-500"
                to={tarefasContatoHref}
                onClick={() => setOpenDropdown(null)}
              >
                Ver todas as tarefas
                {tarefasPendentesTotal > MAX_TAREFAS_NO_SINO && ` (${tarefasPendentesTotal})`}
              </Link>
            </div>
          </li>
        )}

        <li className="nav-item mx-1">
          <Link
            className="nav-link"
            to="/novidades"
            title={novidadesNaoLidas.length ? `${novidadesNaoLidas.length} novidade(s) não lida(s)` : 'Novidades do sistema'}
          >
            <i className="fas fa-newspaper fa-fw" />
            {novidadesNaoLidas.length > 0 && (
              <span className="badge badge-primary badge-counter">{novidadesNaoLidas.length}</span>
            )}
          </Link>
          <NovidadesPopup />
        </li>

        {hasPermissao('menu.ajuda') && (
          <li className="nav-item mx-1">
            <Link className="nav-link" to="/ajuda" title="Ajuda">
              <i className="fas fa-question-circle fa-fw" />
            </Link>
          </li>
        )}

        <li className="nav-item mx-1">
          <a
            className="nav-link"
            href={URL_SUPORTE}
            target="_blank"
            rel="noopener noreferrer"
            title="Solicite ajuda ou envie sugestões"
          >
            <i className="fab fa-whatsapp fa-fw" />
          </a>
        </li>

        <div className="topbar-divider d-none d-sm-block" />

        <li className="nav-item dropdown no-arrow">
          <a
            className="nav-link dropdown-toggle"
            href="#userDropdown"
            id="userDropdown"
            role="button"
            onClick={toggleDropdown('perfil')}
            aria-haspopup="true"
            aria-expanded={openDropdown === 'perfil'}
          >
            <span className="mr-2 d-none d-lg-inline text-gray-600 small">{name}</span>
            {image
              ? <img src={image} alt="Foto do usuário" className="img-profile rounded-circle" />
              : <i className="fas fa-user-circle fa-fw fa-lg" />}
          </a>
          <div
            className={`dropdown-menu dropdown-menu-right shadow animated--grow-in${openDropdown === 'perfil' ? ' show' : ''}`}
            aria-labelledby="userDropdown"
          >
            <Link className="dropdown-item" to="/view-profile" onClick={() => setOpenDropdown(null)}>
              <i className="fas fa-user fa-sm fa-fw mr-2 text-gray-400" />
              Perfil
            </Link>
            <div className="dropdown-divider" />
            <Link
              className="dropdown-item"
              to="/"
              onClick={() => { setOpenDropdown(null); handleLogout(); }}
            >
              <i className="fas fa-sign-out-alt fa-sm fa-fw mr-2 text-gray-400" />
              Sair
            </Link>
          </div>
        </li>
      </ul>
    </nav>
  );
}
